import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { it, enUS } from 'date-fns/locale';
import { UserRelationshipType } from '@timesheet/schemas';
import { ArrowLeft, ChevronLeft, ChevronRight, ClipboardCheck, Download, Loader2 } from 'lucide-react';
import { capitalize } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { PageTransition } from '../components/PageTransition';
import { EmptyState } from '../components/shared/empty-state';
import { useNotification } from '../contexts/NotificationContext';
import { MonthCalendar, PendingApproval, timesheetService } from '../services/timesheetService';
import { userService } from '../services/userService';
import { clientSupplierService } from '../services/clientSupplierService';
import { buildRows, groupRowsByType, monthKey, offDaysByKey, parseMonthKey } from '../components/approvals/approvals.utils';
import {
  MonthCalendarLine,
  MonthDecisionBar,
  MonthLedger,
  MonthStatusPill,
  MonthSummary,
  useEntriesLabel,
  usePendingSelection,
} from '../components/approvals/TimesheetMonthDetail';
import { useApprovalDecisions } from '../components/approvals/useApprovalDecisions';

const fileSlug = (name: string) =>
  name.trim().replace(/[^a-zA-Z0-9_-]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') || 'user';

/**
 * One person's month, full page: `/approvals/:userId?month=yyyy-MM`. The "open the full timesheet"
 * destination from the approvals popup, and the link a submission notification points to. Same
 * data as the list (management endpoint), so the numbers never disagree; previous/next person moves
 * through the people of the same relationship type, in list order.
 */
const ApprovalPersonMonth: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith('it') ? it : enUS;
  const navigate = useNavigate();
  const { userId: userIdParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showNotification } = useNotification();
  const entriesLabel = useEntriesLabel();

  const userId = Number(userIdParam);
  const today = new Date();
  const { year, month } = parseMonthKey(searchParams.get('month')) ?? {
    year: today.getFullYear(),
    month: today.getMonth() + 1,
  };
  const selectedMonthKey = monthKey(year, month);
  const monthLabel = capitalize(format(new Date(year, month - 1, 1), 'LLLL yyyy', { locale: dateLocale }));

  const [groups, setGroups] = useState<PendingApproval[]>([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<Array<{ id: number; name: string; supplierId?: number | null; relationshipType?: UserRelationshipType }>>([]);
  const [companies, setCompanies] = useState<Array<{ id: number; name: string }>>([]);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [calendar, setCalendar] = useState<MonthCalendar | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await timesheetService.getManagementTimesheets(year, month);
      setGroups(
        data.map((group) => ({
          ...group,
          stats: {
            workedDays: Number(group?.stats?.workedDays) || 0,
            vacationDays: Number(group?.stats?.vacationDays) || 0,
            onCallDays: Number(group?.stats?.onCallDays) || 0,
            permissionDays: Number(group?.stats?.permissionDays) || 0,
            sicknessDays: Number(group?.stats?.sicknessDays) || 0,
            rolDays: Number(group?.stats?.rolDays) || 0,
          },
        })),
      );
    } catch (error) {
      console.error('Failed to fetch timesheets:', error);
      showNotification(t('approvals.errors.fetch', 'Failed to load timesheets'), 'error');
    } finally {
      setLoading(false);
    }
  }, [year, month, showNotification, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Working days, expected hours and off days of the month (holidays and company closings)
  useEffect(() => {
    let cancelled = false;
    setCalendar(null);
    timesheetService
      .getManagementMonthCalendar(year, month)
      .then((data) => {
        if (!cancelled) setCalendar(data);
      })
      .catch((error) => console.error('Failed to fetch month calendar:', error));
    return () => {
      cancelled = true;
    };
  }, [year, month]);

  useEffect(() => {
    Promise.all([userService.getAll(), clientSupplierService.getAll()])
      .then(([usersData, companiesData]) => {
        setUsers(usersData.map((u) => ({ id: u.id, name: u.name, supplierId: u.supplierId, relationshipType: u.relationshipType })));
        setCompanies(companiesData.map((c) => ({ id: c.id, name: c.name })));
      })
      .catch((error) => console.error('Failed to fetch users/companies:', error));
  }, []);

  const usersById = useMemo(() => new Map(users.map((user) => [user.id, user])), [users]);
  const companyNameById = useMemo(() => new Map(companies.map((company) => [company.id, company.name])), [companies]);
  const offDays = useMemo(() => offDaysByKey(calendar), [calendar]);

  const rows = useMemo(() => buildRows(groups, (id) => usersById.get(id)?.relationshipType), [groups, usersById]);
  const row = useMemo(() => rows.find((candidate) => candidate.userId === userId) ?? null, [rows, userId]);
  const siblings = useMemo(() => (row ? groupRowsByType(rows)[row.relationshipType] : []), [rows, row]);
  const position = siblings.findIndex((candidate) => candidate.userId === userId);
  const previousPerson = position > 0 ? siblings[position - 1] : null;
  const nextPerson = position >= 0 && position < siblings.length - 1 ? siblings[position + 1] : null;

  const selection = usePendingSelection(row);
  const { processing, beginApprove, beginReject, dialogs: decisionDialogs } = useApprovalDecisions({
    rows,
    monthKey: selectedMonthKey,
    usersById,
    refetch: fetchData,
  });

  const personName = row?.name ?? usersById.get(userId)?.name ?? `#${userId}`;
  const relationshipLabel = (type: UserRelationshipType) => t(`pages.usersManager.relationshipTypes.${type}`, type);
  const supplierId = Number(usersById.get(userId)?.supplierId);
  const companyName = supplierId > 0 ? companyNameById.get(supplierId) : undefined;
  const subtitle = [row && relationshipLabel(row.relationshipType), companyName].filter(Boolean).join(' · ');

  const goToMonth = (offset: number) => {
    const target = new Date(year, month - 1 + offset, 1);
    setSearchParams({ month: monthKey(target.getFullYear(), target.getMonth() + 1) }, { replace: true });
  };
  const goToPerson = (id: number) => navigate(`/approvals/${id}?month=${selectedMonthKey}`);
  const backToList = () => navigate(`/approvals?month=${selectedMonthKey}${row ? `&tab=${row.relationshipType}` : ''}`);

  const handleDownloadPdf = async () => {
    try {
      setDownloadingPdf(true);
      const blob = await timesheetService.downloadManagementPdf(year, month, { userId });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `timesheet-${fileSlug(personName)}-${selectedMonthKey}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download PDF:', error);
      showNotification(t('approvals.errors.downloadPdf', 'Failed to download PDF'), 'error');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const selectedCount = selection.selected.size;
  const selectedLabel =
    selectedCount === 1
      ? t('approvals.person.selectedOne', '1 voce selezionata')
      : t('approvals.person.selectedOther', '{{count}} voci selezionate', { count: selectedCount });

  return (
    <PageTransition>
      <div className="space-y-5 p-4 sm:p-6">
        <Button type="button" variant="ghost" size="sm" className="-ml-2 gap-1.5 text-muted-foreground" onClick={backToList}>
          <ArrowLeft className="h-4 w-4" />
          {t('approvals.person.backToList', 'Torna a Gestione timesheet')}
        </Button>

        {/* Header: the person is the headline; the month and the export sit on the right */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="space-y-1">
            <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-orange-600 dark:text-orange-400">
              <ClipboardCheck className="h-3.5 w-3.5" />
              {t('approvals.person.kicker', 'Timesheet del mese')}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label={t('approvals.person.previous', 'Persona precedente')}
                title={previousPerson?.name}
                disabled={!previousPerson}
                onClick={() => previousPerson && goToPerson(previousPerson.userId)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <h1 className="text-3xl font-semibold tracking-tight">{personName}</h1>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label={t('approvals.person.next', 'Persona successiva')}
                title={nextPerson?.name}
                disabled={!nextPerson}
                onClick={() => nextPerson && goToPerson(nextPerson.userId)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              {row && <MonthStatusPill pendingCount={row.pendingIds.length} />}
            </div>
            {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
            <MonthCalendarLine calendar={calendar} />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8" aria-label={t('approvals.month.previous', 'Mese precedente')} onClick={() => goToMonth(-1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="min-w-[10ch] text-center text-sm font-medium tabular-nums">{monthLabel}</span>
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8" aria-label={t('approvals.month.next', 'Mese successivo')} onClick={() => goToMonth(1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button type="button" variant="outline" size="sm" className="gap-2" onClick={handleDownloadPdf} disabled={loading || downloadingPdf || !row}>
              {downloadingPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {t('approvals.downloadPdf', 'Esporta PDF')}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="space-y-3 rounded-lg border bg-card p-5">
            <Skeleton className="h-4 w-64" />
            <Skeleton className="h-4 w-40" />
            {Array.from({ length: 6 }).map((_, index) => (
              <Skeleton key={index} className="h-8 w-full" />
            ))}
          </div>
        ) : !row ? (
          <div className="rounded-lg border border-dashed bg-card">
            <EmptyState
              icon={ClipboardCheck}
              title={t('approvals.person.empty.title', 'Nessun timesheet per {{name}}', { name: personName })}
              description={t('approvals.person.empty.description', "Nessuna voce inviata o approvata a {{month}}. Cambia mese o torna all'elenco.", { month: monthLabel })}
              action={{ label: t('approvals.person.backToList', 'Torna a Gestione timesheet'), onClick: backToList, icon: ArrowLeft }}
            />
          </div>
        ) : (
          <>
            <div className="overflow-hidden rounded-lg border bg-card">
              <MonthSummary row={row} calendar={calendar} className="border-b px-4 py-4 sm:px-5" />
              <div className="px-2 pb-2 sm:px-3">
                <MonthLedger
                  row={row}
                  selection={selection}
                  allDays={{ year, month, offDay: (dateKey) => offDays.get(dateKey) }}
                />
              </div>
            </div>

            {/* Decisions stay in view while the ledger scrolls */}
            <div className="sticky bottom-4 z-20 rounded-lg border bg-card px-3 py-2.5 shadow-lg">
              <MonthDecisionBar
                pendingCount={row.pendingIds.length}
                selectedCount={selectedCount}
                processing={processing}
                onApprove={() => beginApprove(Array.from(selection.selected))}
                onReject={() => beginReject(Array.from(selection.selected))}
                leading={
                  row.pendingIds.length > 0 ? (
                    <span className="text-sm font-medium tabular-nums">
                      {selectedLabel}
                      <span className="text-muted-foreground"> · {entriesLabel(row.pendingIds.length)} {t('approvals.person.pendingSuffix', 'da approvare')}</span>
                    </span>
                  ) : null
                }
              />
            </div>
          </>
        )}

        {decisionDialogs}
      </div>
    </PageTransition>
  );
};

export default ApprovalPersonMonth;
